import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConsumptionTrendReport,
  exportConsumptionTrendReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchAllInventorySubcategories } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { TrendingUp, AlertTriangle, Calendar, Package, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import FormField from 'components/common/FormField';

const ConsumptionTrendPage = () => {
  const dispatch = useDispatch();

  const {
    consumptionTrendReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = consumptionTrendReport?.data?.summary || {};
  const results = consumptionTrendReport?.data?.results || [];
  const pagination = consumptionTrendReport?.pagination || {};

  const subcategories = useSelector((state) => state.common?.allInventorySubcategories.items || []);


  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);
  
  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    sub_category: 'all',
    total_consumed_gte: '',
    recent_consumed_gte: '',
    average_daily_gte: '',
    spike_detected: 'all',
  });

  // Calculate total pages from server response
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value => 
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'consumption_trend',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
      ...(advancedFilters.total_consumed_gte && { total_consumed__gte: advancedFilters.total_consumed_gte }),
      ...(advancedFilters.recent_consumed_gte && { recent_consumed__gte: advancedFilters.recent_consumed_gte }),
      ...(advancedFilters.average_daily_gte && { average_daily__gte: advancedFilters.average_daily_gte }),
      ...(advancedFilters.spike_detected !== 'all' && { spike_detected: advancedFilters.spike_detected }),
    };
    

    dispatch(fetchConsumptionTrendReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all subcategories on component mount
  useEffect(() => {
    dispatch(fetchAllInventorySubcategories()); // Empty string to get all subcategories
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      sub_category: 'all',
      total_consumed_gte: '',
      recent_consumed_gte: '',
      average_daily_gte: '',
      spike_detected: 'all',
    });
  };

  const columns = [
    { accessorKey: 'Sub Category', header: 'Sub Category' },
    {
      accessorKey: 'Total Consumed',
      header: 'Total Consumed',
      cell: ({ row }) => (
        <span className="font-semibold">{row.original['Total Consumed'] ?? 0}</span>
      ),
    },
    {
      accessorKey: 'Recent Consumed (7d)',
      header: 'Recent (Last 7 Days)',
      cell: ({ row }) => (
        <span className="font-medium text-orange-600">
          {row.original['Recent Consumed (7d)'] ?? 0}
        </span>
      ),
    },
    {
      accessorKey: 'Average Daily',
      header: 'Avg Daily Consumption',
      cell: ({ row }) => (
        <span>{(row.original['Average Daily'] ?? 0).toFixed(2)}</span>
      ),
    },
    {
      accessorKey: 'Spike Detected',
      header: 'Spike Detected',
      cell: ({ row }) => {
        const spike = row.original['Spike Detected'];
        return (
          <Badge variant={spike === 'Yes' ? 'destructive' : 'secondary'}>
            {spike}
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Consumption Trend Report"
        description="Analyze historical and recent consumption patterns with spike detection"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="sub_category">Sub Category</Label>
                <Select
                  value={advancedFilters.sub_category}
                  onValueChange={(value) => handleAdvancedFilterChange('sub_category', value)}
                 
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(subcategories) && subcategories.map((subcategory) => (
                      <SelectItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}
              <FormField
  label="Sub Category"
  name="sub_category"
  type="select"
  value={advancedFilters.sub_category}
  onChange={(e) =>
    handleAdvancedFilterChange('sub_category', e.target.value)
  }
  options={[
    { value: 'all', label: 'All' },
    ...(Array.isArray(subcategories)
      ? subcategories.map((subcategory) => ({
          value: String(subcategory.id), // keep value as string
          label: subcategory.name,
        }))
      : []),
  ]}
/>

              
              <div className="space-y-2">
                <Label htmlFor="spike_detected">Spike Detected</Label>
                <Select
                  value={advancedFilters.spike_detected}
                  onValueChange={(value) => handleAdvancedFilterChange('spike_detected', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Total Consumed (Min)</Label>
                <Input
                  type="number"
                  placeholder="Minimum"
                  value={advancedFilters.total_consumed_gte}
                  onChange={(e) => handleAdvancedFilterChange('total_consumed_gte', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Recent Consumed (7 Days, Min)</Label>
                <Input
                  type="number"
                  placeholder="Minimum"
                  value={advancedFilters.recent_consumed_gte}
                  onChange={(e) => handleAdvancedFilterChange('recent_consumed_gte', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Average Daily (Min)</Label>
                <Input
                  type="number"
                  placeholder="Minimum"
                  value={advancedFilters.average_daily_gte}
                  onChange={(e) => handleAdvancedFilterChange('average_daily_gte', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Consumed Item</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold truncate">
              {summary['Top Consumed Item'] || 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Consumption</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary['Total Consumption'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Spikes Detected</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary['Spikes Detected'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Historical Period</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary['Historical Days'] ?? 0} days
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading consumption trend data...</span>
        </div>
      ) : ( */}
        <DataTable
          data={results} // Use the paginated results from the server
          columns={columns}
          totalCount={pagination.count}
          searchPlaceholder="Search by sub-category..."
          onSearch={setSearchTerm}
          showSearch={true}
          pageIndex={page - 1} // Convert to 0-based index for DataTable
          pageSize={pageSize}
          pageCount={totalPages} // Use total pages from server response
          onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          isLoading={isLoading}
          onExport={() =>
            dispatch(exportConsumptionTrendReport({ 
              filters: { 
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: 'consumption_trend',
                ...dateFilters,
                // Include advanced filters in export
                ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
                ...(advancedFilters.total_consumed_gte && { total_consumed__gte: advancedFilters.total_consumed_gte }),
                ...(advancedFilters.recent_consumed_gte && { recent_consumed__gte: advancedFilters.recent_consumed_gte }),
                ...(advancedFilters.average_daily_gte && { average_daily__gte: advancedFilters.average_daily_gte }),
                ...(advancedFilters.spike_detected !== 'all' && { spike_detected: advancedFilters.spike_detected }),
              } 
            }))
          }
          showExport={true}
          showImport={false}
        />
      {/* )} */}
    </div>
  );
};

export default ConsumptionTrendPage;